/**
 * GlossaryContext · slot_vocabulary_navbar (17 MAY 2026).
 *
 * Provides the `openGlossary` callback to any descendant without prop-drilling
 * через 8 NavShell-owning screens. State itself still lives in App.tsx Router
 * (`glossaryOpen`) — context exposes only the opener, mirroring how the
 * existing CockpitOverview Sprint-context entry already consumes the same
 * setter via prop chain.
 *
 * Consumers:
 *   - NavShell · intercepts onNav('glossary') from Sidebar/BottomTabs foot item
 *     and calls open() so the App-owned GlossaryOverlay mounts. Mirror of the
 *     'atelier' intercept pattern (NavShell line ~110), but state is one level
 *     up so the older Sprint-context entry can keep using its prop callback.
 *
 * Why not also move state here:
 *   - openFlashcards in App.tsx must close glossary as a side-effect
 *     (`setGlossaryOpen(false); setFlashcardsOpen(true)`). Keeping both flags
 *     co-located in Router preserves that coordination without a second context.
 *   - GlossaryOverlay also accepts `onOpenFlashcards` — same coupling reason.
 */

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

export interface GlossaryContextValue {
  /** Opens the glossary overlay. Idempotent · safe to call when already open. */
  open: () => void;
}

const noop = () => {
  /* default · used when no provider is mounted (e.g. isolated component tests). */
};

const GlossaryContext = createContext<GlossaryContextValue>({ open: noop });

export interface GlossaryProviderProps {
  open: () => void;
  children: ReactNode;
}

export function GlossaryProvider({ open, children }: GlossaryProviderProps) {
  return (
    <GlossaryContext.Provider value={{ open }}>{children}</GlossaryContext.Provider>
  );
}

/** Hook · returns the glossary context. Falls back к noop if no provider. */
export function useGlossary(): GlossaryContextValue {
  return useContext(GlossaryContext);
}
